import {useFrame} from '@react-three/fiber';
import {useXR} from '@react-three/xr';
import {useState} from 'react';
import {Vector3} from 'three';

// Global store for ball position offset (shared across exercises)
export const ballPositionStore = {
  offset: {x: 0, y: 0, z: 0},
  setOffset: (offset: {x: number; y: number; z: number}) => {
    ballPositionStore.offset = offset;
  },
  getOffset: () => ballPositionStore.offset,
};

export const useXRController = () => {
  const [positionOffset, setPositionOffset] = useState(new Vector3(0, 0, 0));
  // State to track if the directional menu is open (local to the hook's consumer context)
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Check if we're in XR mode
  const xrState = useXR();
  const isInXR = !!xrState.session;

  // Handle position offset changes from the TrainingTarget
  const handlePositionChange = (newOffset: Vector3) => {
    setPositionOffset(newOffset);
    // Update global store
    ballPositionStore.setOffset({x: newOffset.x, y: newOffset.y, z: newOffset.z});
  };

  // Function to handle menu state changes (only manages isMenuOpen locally)
  const handleMenuToggle = (isOpen: boolean) => {
    setIsMenuOpen(isOpen);
    console.log(`XRController: Menu toggled: ${isOpen ? 'open' : 'closed'}`);
  };

  // Handle XR controller input for position adjustment
  useFrame(() => {
    if (!isInXR || !xrState.session) return;

    // Access input sources from the session
    const inputSources = xrState.session.inputSources;
    if (!inputSources || inputSources.length === 0) return;

    let xAdjust = 0,
      yAdjust = 0,
      zAdjust = 0;

    // Process each controller input
    inputSources.forEach(inputSource => {
      if (inputSource.gamepad) {
        const gamepad = inputSource.gamepad;
        const handedness = inputSource.handedness;

        // Use right controller for X and Z adjustment
        if (handedness === 'right' && gamepad.axes.length >= 4) {
          const xAxis = gamepad.axes[2]; // Right thumbstick X
          const zAxis = gamepad.axes[3]; // Right thumbstick Y

          if (Math.abs(xAxis) > 0.1) {
            // Apply a small deadzone
            xAdjust = xAxis * 0.01;
          }

          if (Math.abs(zAxis) > 0.1) {
            // Apply a small deadzone
            zAdjust = -zAxis * 0.01; // Negative for intuitive control
          }
        }

        // Use left controller for Y adjustment
        if (handedness === 'left' && gamepad.axes.length >= 2) {
          const yAxis = gamepad.axes[1]; // Left thumbstick Y

          if (Math.abs(yAxis) > 0.1) {
            // Apply a small deadzone
            yAdjust = -yAxis * 0.01; // Negative for intuitive control
          }
        }
      }
    });

    // Update position offset if any adjustments were made
    if (xAdjust !== 0 || yAdjust !== 0 || zAdjust !== 0) {
      setPositionOffset(current => {
        const newOffset = new Vector3(current.x + xAdjust, current.y + yAdjust, current.z + zAdjust);
        // Update global store
        ballPositionStore.setOffset({x: newOffset.x, y: newOffset.y, z: newOffset.z});
        return newOffset;
      });
    }
  });

  return {
    positionOffset,
    isInXR,
    isMenuOpen,
    handlePositionChange,
    handleMenuToggle,
  };
};
